import { isWallExteriorFinishBRep } from "building-common/analyticEntity/buildingAnalyticEntitySelectors";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticBRep, BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import { isBuildingGeomParentTag, isBuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTags";
import { BuildingRoofAnalyticConditions } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { SOFFIT } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { Table } from "common/data/table";
import { notUndefinedOrNull } from "common/graph/entityGraph";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { docEntityHandleIsGeneratedBuildingEntityHandle } from "doc/entity/docEntityHandleTools";
import { materialSrcFromPalette } from "doc/materialLookup";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb3Grow } from "geom/aabb3";
import { faceAABB3 } from "geom/brep";
import { planeContainsPolyLine3Edges, polyLine2OnPlane } from "geom/plane";
import { unionCoplanarFaces } from "geom/unionFaces";
import { addFaces } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";
import { analyticConditionColors } from "./generateRoofSystemAnalyticTools";
import { hashMaterialWithUV } from "./hashMaterialWithUV";

type SoffitFaceArray = {
  faces: FaceArrayViewEntity;
  soffitHandle: DocEntityHandle;
  adjacent: Set<BuildingAnalyticBRep>;
};

const ROOF_SOFFIT_OPACITY = percentNew(0.1);

const ROOF_SOFFIT_STYLE = {
  color: analyticConditionColors[BuildingRoofAnalyticConditions.SOFFIT],
  opacity: percentNew(ROOF_SOFFIT_OPACITY),
  lineWidth3d: worldInchesNew(0.1),
};

const ADJACENT_FACE_TOLERANCE = 0.25;

export function addAllRoofSoffitAnalyticEntities(building: ReturnType<typeof select>): Table<BuildingAnalyticEntity> {
  const { exteriorPalette, roofSoffits } = building;
  const entities: Table<BuildingAnalyticEntity> = {};

  // collect soffit faces by parent and material so they can be unioned
  const soffitFacesByHash: { [key: string]: SoffitFaceArray } = {};
  for (const soffit of Object.values(roofSoffits)) {
    const soffitMaterialHash = hashMaterialWithUV(soffit.material, soffit.materialTransformation);

    for (const face of soffit.faces) {
      if (!face.tags) {
        continue;
      }

      // filter out anything that is not a bottom face
      if (!face.tags.some((t) => isBuildingGeomTag(t) && t.type === "POSITION" && t.position === "BOTTOM")) {
        continue;
      }

      // filter out anything that is not a soffit face
      const parentSoffitTags = face.tags
        ?.filter(isBuildingGeomParentTag)
        .filter((t) => t.parentType === "ROOF_SOFFIT")
        .reduce((acc, curr) => {
          if (curr) {
            acc.add(curr.parentId);
          }
          return acc;
        }, new Set<string>());
      if (!parentSoffitTags) {
        continue;
      }

      const soffitLoop3 = polyLine2OnPlane(face.plane, face.loops[0]);

      // find adjacent exterior finish BReps
      const adjacentSearchAABB3 = faceAABB3(face);
      aabb3Grow(adjacentSearchAABB3, ADJACENT_FACE_TOLERANCE, ADJACENT_FACE_TOLERANCE, ADJACENT_FACE_TOLERANCE);
      const adjacentFinishes = BuildingGenerationContext.indexSpatialSearch(adjacentSearchAABB3)
        .filter(docEntityHandleIsGeneratedBuildingEntityHandle)
        .map((h) => h.entity)
        .filter(isBuildingEntityHandle)
        .filter((handle) => isBuildingAnalyticEntityType(handle.entityType))
        .map((h) => building.analyticWallEntities[h.id])
        .filter(notUndefinedOrNull)
        .filter(isWallExteriorFinishBRep)
        .filter((brep) => {
          const primaryFace = brep.brep.faces[0];
          return (
            !!primaryFace &&
            planeContainsPolyLine3Edges(primaryFace.plane, soffitLoop3, ADJACENT_FACE_TOLERANCE).length > 0
          );
        });

      for (const parentId of parentSoffitTags) {
        const soffitFaceHash = parentId + "." + soffitMaterialHash;
        if (!soffitFacesByHash[soffitFaceHash]) {
          const soffitHandle: GeneratedEntityHandle = {
            type: "GENERATED_ENTITY",
            entity: {
              entityType: "ROOF_SOFFIT",
              id: parentId,
            },
          };
          soffitFacesByHash[soffitFaceHash] = {
            soffitHandle,
            faces: faceArrayViewEntityNew([face], undefined, soffit.material, undefined, soffit.materialTransformation),
            adjacent: new Set(adjacentFinishes),
          };
        } else {
          soffitFacesByHash[soffitFaceHash].faces.faces.push(face);
          for (const adjacentFinish of adjacentFinishes) {
            soffitFacesByHash[soffitFaceHash].adjacent.add(adjacentFinish);
          }
        }
      }
    }
  }

  for (const { faces, soffitHandle, adjacent } of Object.values(soffitFacesByHash)) {
    const analyticFaces = addFaces(
      "ANALYTIC_ROOF_SOFFIT",
      entities,
      "Soffit Face",
      [soffitHandle],
      unionCoplanarFaces(faces.faces, 0),
      {
        materialSrc: faces.material ? materialSrcFromPalette(exteriorPalette, faces.material) : undefined,
        materialTransformation: faces.materialTransformation,
      },
      [SOFFIT],
      ROOF_SOFFIT_STYLE,
    );
    for (const face of analyticFaces) {
      for (const adjacentFinish of adjacent) {
        BuildingGenerationContext.indexRelate(face.docEntityHandle, "ADJACENT_TO", adjacentFinish.docEntityHandle);
      }
    }
  }
  return entities;
}

const select = makeBuildingSelector(["roofSoffits", "exteriorPalette", "analyticWallEntities"]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticRoofSoffitEntities"> {
  return { analyticRoofSoffitEntities: addAllRoofSoffitAnalyticEntities(building) };
}

export default memo(select, generate);
